import { now, toDate } from "common";
import { Dayjs, ManipulateType } from "dayjs";

export const getRelativeDateRange = (interval: string, relativeStartDate: string, initialDate = now()) => {
    const parseRelativeStartDate = relativeStartDateMap[relativeStartDate] ?? ((value: Dayjs) => value);
    const from = parseRelativeStartDate(initialDate);
    const [value, unit] = intervalDurationMap[interval as Interval] ?? intervalDurationMap[Interval.DAY];
    const to = from.add(value, unit);
    return { from: toDate(from), to: toDate(to, false) };
};

export const isInterval = (value: any) => {
    return value in intervalDurationMap;
};

enum Interval {
    DAY = "P1D",
    WEEK = "P1W",
    MONTH = "P1M",
    QUARTER = "P3M",
    YEAR = "P1Y",
}

const intervalDurationMap: Record<Interval, [number, ManipulateType]> = {
    [Interval.DAY]: [1, "day"],
    [Interval.WEEK]: [1, "week"],
    [Interval.MONTH]: [1, "month"],
    [Interval.QUARTER]: [3, "months"],
    [Interval.YEAR]: [1, "year"],
};

const relativeStartDateMap: Record<string, (value: Dayjs) => Dayjs> = {
    yesterday: (value) => {
        return value.subtract(1, "day").startOf("day");
    },

    today: (value) => {
        return value.startOf("day");
    },

    tomorrow: (value) => {
        return value.add(1, "day").startOf("day");
    },

    "last week": (value) => {
        return value.subtract(1, "week").startOf("week");
    },

    "-1 week": (value) => {
        return value.subtract(1, "week").startOf("day");
    },

    "this week": (value) => {
        return value.startOf("week");
    },

    "next week": (value) => {
        return value.add(1, "week").startOf("day");
    },

    "first day of last month": (value) => {
        return value.subtract(1, "month").startOf("month");
    },

    "-1 month": (value) => {
        return value.subtract(1, "month").startOf("day");
    },

    "first day of this month": (value) => {
        return value.startOf("month");
    },

    "first day of next month": (value) => {
        return value.add(1, "month").startOf("month");
    },

    "first day of last quarter": (value) => {
        return value
            .subtract(value.month() % 3, "months")
            .subtract(3, "months")
            .startOf("month");
    },

    "-3 months": (value) => {
        return value.subtract(3, "months").startOf("month");
    },

    "first day of this quarter": (value) => {
        return value.subtract(value.month() % 3, "months").startOf("month");
    },

    "first day of next quarter": (value) => {
        return value
            .subtract(value.month() % 3, "months")
            .add(3, "months")
            .startOf("month");
    },

    "first day of January last year": (value) => {
        return value.subtract(1, "year").startOf("year");
    },

    "-1 year": (value) => {
        return value.subtract(1, "year").startOf("day");
    },

    "first day of this year": (value) => {
        return value.startOf("year");
    },

    "first day of January next year": (value) => {
        return value.add(1, "year").startOf("year");
    },
};
